import { divRoundHalfUp, formatDecimal, parseDecimal } from './decimal.js';

/** Base/Alt quantities are stored as NUMERIC(19,6). */
export const UOM_QTY_SCALE = 6;
/** Counted / converted quantities: NUMERIC(18,4). */
export const QTY_SCALE = 4;
/** Factor snapshot: NUMERIC(18,8). */
export const FACTOR_SCALE = 8;

export interface UomGroupLine {
  uom_code: string;
  base_qty: string;
  alt_qty: string;
}

export interface ItemUom {
  uom_code: string;
  /** factor_to_inventory_uom = num / den (exact) */
  num: bigint;
  den: bigint;
  /** factor rounded to 8 decimals, trailing zeros trimmed */
  factor: string;
  is_inventory: boolean;
  preferred: boolean;
}

/**
 * Units an item can be counted in, with the exact factor to its inventory UoM.
 *
 * SAP B1: 1 AltUoM = BaseQty / AltQty × group base UoM, so
 *   factor(u) = r(u) / r(inventory) = (base_u × alt_inv) / (alt_u × base_inv).
 * If the item has no group, or its inventory UoM is not in the group, only the
 * inventory UoM is available (factor 1) — never guess a factor.
 *
 * Order: inventory UoM, then preferred UoMs in the given order, then the rest of the group.
 */
export function itemUoms(inventoryUom: string, lines: UomGroupLine[] | null | undefined, preferred: string[] = []): ItemUom[] {
  const inv = lines?.find((l) => l.uom_code === inventoryUom);
  if (!lines || !inv) {
    return [{ uom_code: inventoryUom, num: 1n, den: 1n, factor: '1', is_inventory: true, preferred: false }];
  }
  const bInv = parseDecimal(inv.base_qty, UOM_QTY_SCALE);
  const aInv = parseDecimal(inv.alt_qty, UOM_QTY_SCALE);
  if (!bInv || !aInv) throw new Error(`Invalid base/alt quantity for inventory UoM ${inventoryUom}`);

  const out: ItemUom[] = [];
  for (const l of lines) {
    const b = parseDecimal(l.base_qty, UOM_QTY_SCALE);
    const a = parseDecimal(l.alt_qty, UOM_QTY_SCALE);
    if (!b || !a) continue; // factor must be > 0
    const num = b * aInv;
    const den = a * bInv;
    out.push({
      uom_code: l.uom_code,
      num,
      den,
      factor: formatDecimal(divRoundHalfUp(num * 10n ** BigInt(FACTOR_SCALE), den), FACTOR_SCALE, true),
      is_inventory: l.uom_code === inventoryUom,
      preferred: preferred.includes(l.uom_code),
    });
  }
  const rank = (u: ItemUom) => (u.is_inventory ? -1 : u.preferred ? preferred.indexOf(u.uom_code) : 1_000_000);
  return out.sort((x, y) => rank(x) - rank(y));
}

/** Convert a counted quantity string to inventory UoM, rounded half-up to 4 decimals. */
export function convertQty(qty: string, uom: Pick<ItemUom, 'num' | 'den'>): string {
  const q = parseDecimal(qty, QTY_SCALE);
  if (q == null) throw new Error(`Invalid quantity: ${qty}`);
  return formatDecimal(divRoundHalfUp(q * uom.num, uom.den), QTY_SCALE);
}
